// @react
import { useEffect, useContext } from 'react'
// @types
import {
  GetUserQueryResult,
  UserAddressDeleteMutation,
  UserAddressDeleteMutationVariables,
  UserContactInfoUpdateMutation,
  UserContactInfoUpdateMutationVariables,
  UserPhoneDeleteMutation,
  UserPhoneDeleteMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
import { ContactFormValues } from 'modules/common/forms/types/contact/'
// @graphQL
import { useMutation } from '@apollo/client'
import {
  USER_ADDRESS_DELETE,
  USER_CONTACT_INFO_UPDATE,
  USER_PHONE_DELETE,
} from 'graphql/mutations/user.mutation'
// @context
import { SnackbarContext } from 'modules/common/context/snackbar'
// @libraries
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { vsprintf } from 'sprintf-js'
import countryISOCode from 'i18n-iso-countries'
import libphonenumber from 'google-libphonenumber'
// @commons
import strings from 'lib/constants/strings'
// @components
// @module component
import { validationSchema } from 'modules/common/forms/validations/contact'

// Get an instance of `PhoneNumberUtil`.
const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()

type SelfProps = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: () => void
}
type PropsType = SelfProps

const Contact = ({ data: userData, isLoading, refetch }: PropsType) => {
  /****************************************
   * STATE
   *****************************************/

  /******************************************
   * HOOKS
   ******************************************/

  // React Hook Form
  const formMethods = useForm<ContactFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      primaryEmailAddress: '',
      phones: [
        {
          phoneId: '',
          isPrimary: true,
          countryCode: '',
          areaCode: '',
          number: '',
          type: '',
          location: '',
          doNotCall: false,
          // smsText: false,
        },
      ],
      addresses: [
        {
          addressId: '',
          isPrimary: true,
          addressType: '',
          // companyName: '',
          countryCode: '',
          address1: '',
          address2: '',
          address3: '',
          city: '',
          postalCode: '',
          state: '',
          // isPostalMailOptedOut: false,
        },
      ],
    },
  })
  const { control, reset } = formMethods
  const user = userData?.user
  const {
    formState: { dirtyFields },
  } = formMethods
  const isFormDirty = Object.keys(dirtyFields).length > 0

  useEffect(() => {
    if (user) {
      reset({
        primaryEmailAddress: user?.contactInfo.primaryEmailAddress || '',
        phones: user?.contactInfo.phones?.length
          ? user?.contactInfo.phones?.map((phone) => {
              return {
                phoneId: phone.id || '',
                isPrimary: phone.isPrimary || false,
                countryCode: phone.phoneCountryCode || '1',
                areaCode: phone.phoneAreaCode || '',
                number: `${phone.phoneAreaCode}${phone.phoneNumber}` || '',
                type: phone.commTypeCodeString || '',
                location: phone.commLocationCodeString || '',
                doNotCall: phone.doNotCallFlag || false,
              }
            })
          : [
              {
                phoneId: '',
                isPrimary: false,
                countryCode: '',
                areaCode: '',
                number: '',
                type: '',
                location: '',
                doNotCall: false,
              },
            ],
        addresses: user?.contactInfo.address?.length
          ? user?.contactInfo.address?.map((addr) => {
              return {
                addressId: addr.id || '',
                addressType: addr.addressType || '',
                isPrimary: addr.isPrimary || false,
                countryCode:
                  (addr.countryCode &&
                    countryISOCode.alpha3ToAlpha2(addr.countryCode)) ||
                  '',
                address1: addr.address1 || '',
                address2: addr.address2 || '',
                address3: addr.address3 || '',
                city: addr.city || '',
                postalCode: addr.postalCode || '',
                state: addr.state || '',
              }
            })
          : [
              {
                addressId: '',
                addressType: '',
                isPrimary: false,
                countryCode: '',
                address1: '',
                address2: '',
                address3: '',
                city: '',
                postalCode: '',
                state: '',
              },
            ],
      })
    }
  }, [reset, user])

  /******************************************
   * GQL HOOKS
   ******************************************/

  const [updateUserContactInfo] = useMutation<
    UserContactInfoUpdateMutation,
    UserContactInfoUpdateMutationVariables
  >(USER_CONTACT_INFO_UPDATE)

  const [deleteUserAddress] = useMutation<
    UserAddressDeleteMutation,
    UserAddressDeleteMutationVariables
  >(USER_ADDRESS_DELETE)

  const [deleteUserPhone] = useMutation<
    UserPhoneDeleteMutation,
    UserPhoneDeleteMutationVariables
  >(USER_PHONE_DELETE)

  /******************************************
   * HOOKS
   ******************************************/
  const snackbar = useContext(SnackbarContext)

  /****************************************
   * FORM HANDLERS
   *****************************************/

  const userContactInfoUpdateHandler = async (
    data: ContactFormValues,
  ): Promise<boolean> => {
    let phones
    /**
     * format data
     */
    const address = data.addresses.map((item, index) => {
      const countryCode =
        item.countryCode.length === 2
          ? countryISOCode.alpha2ToAlpha3(item.countryCode)
          : item.countryCode

      return {
        addressId: item.addressId,
        isPrimary: index === 0, // item.isPrimary,
        address1: item.address1,
        address2: item.address2,
        address3: item.address3,
        addressType: item.addressType,
        city: item.city,
        state: item.state,
        countryCode,
        postalCode: item.postalCode,
        //  companyName: item.companyName, //@todo is this viable?
      }
    })

    if (dirtyFields.phones?.length) {
      try {
        phones = data.phones.map((item, index) => {
          const number = item.number.replace(/[^\d]/g, '')
          const areaCode = number.substring(0, 3)
          const numberOrValue = number.substring(3, 10)
          const parsedNumber = phoneUtil.parseAndKeepRawInput(
            `+${item.countryCode}${number}`,
          )
          const regionCode = phoneUtil.getRegionCodeForNumber(parsedNumber)

          const newRegionCode = regionCode === 'CA' ? 'US' : regionCode

          if (!newRegionCode) {
            console.log('no region code')
            throw {}
          }
          const formattedRegionCode = countryISOCode.alpha2ToAlpha3(
            newRegionCode,
          )

          if (!formattedRegionCode) {
            console.log('no formatted region code')
            throw {}
          }

          return {
            phoneId: `${item.type}_${item.location}`,
            isPrimary: index === 0, // item.isPrimary
            doNotCallFlag: item.doNotCall,
            countryCode: formattedRegionCode,
            phoneCountryCode: item.countryCode.replace(/[+ ]/g, ''),
            phoneAreaCode: areaCode, // @todo we should break it apart here if we can since we have access to individual fields
            // primarySMSText: item.smsText, // @todo removed
            phoneNumber: numberOrValue,
            commTypeCodeString: item.type,
            commLocationCodeString: item.location,
            phoneExtension: '',
          }
        })
      } catch (e) {
        console.log(e)
        snackbar.setMessage({
          type: SnackbarTypes.ERROR,
          message: 'This is not a valid phone number',
          show: true,
        })
        return false
      }
    }

    /**
     * execute query
     */
    const { errors: mutationErrors } = await updateUserContactInfo({
      variables: {
        input: {
          primaryEmailAddress: data.primaryEmailAddress,
          address,
          phones,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Address',
        ]),
        show: true,
      })
      await refetch()
      return true
    }

    snackbar.setMessage({
      type: SnackbarTypes.ERROR,
      message: strings.ERRORS.GENERIC_UPDATE_ERROR,
      show: true,
    })
    console.log('mutation error', mutationErrors)

    return false
  }

  /**
   *
   * @param id
   */
  const userAddressDeleteHandler = async (id: number): Promise<boolean> => {
    const values = formMethods.getValues()
    const address = values.addresses[id]
    const addressId = values.addresses[id]?.addressId
    const countryCode =
      address?.countryCode?.length === 2
        ? countryISOCode.alpha2ToAlpha3(address.countryCode)
        : address.countryCode

    if (addressId) {
      const { errors: mutationErrors } = await deleteUserAddress({
        variables: {
          input: {
            addressId,
            address1: address.address1,
            city: address.city,
            countryCode,
            postalCode: address.postalCode,
            state: address.state,
          },
        },
      })

      if (!mutationErrors) {
        snackbar.setMessage({
          type: SnackbarTypes.SUCCESS,
          message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
            'Address',
          ]),
          show: true,
        })
        return true
      }
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
      return false
    }

    // if there is no address id, we can just remove the element right away
    return true
  }

  /**
   *
   * @param id
   */
  const userPhoneDeleteHandler = async (id: number): Promise<boolean> => {
    const values = formMethods.getValues()
    const phone = values.phones[id]
    const phoneId = values.phones[id]?.phoneId

    if (phoneId) {
      const { errors: mutationErrors } = await deleteUserPhone({
        variables: {
          input: {
            commTypeCodeString: phone.type,
            commLocationCodeString: phone.location,
          },
        },
      })

      if (!mutationErrors) {
        snackbar.setMessage({
          type: SnackbarTypes.SUCCESS,
          message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
            'Phone number',
          ]),
          show: true,
        })
        // update the UI to optimistically remove the education item from data array
        return true
      } else {
        snackbar.setMessage({
          type: SnackbarTypes.ERROR,
          message: strings.ERRORS.GENERIC_UPDATE_ERROR,
          show: true,
        })
        console.log('mutation error', mutationErrors)
        return false
      }
    }

    // if there is no phone id, we can just remove the element right away
    return true
  }

  /******************************************
   * RENDER
   ******************************************/

  return {
    formMethods,
    control,
    userPhoneDeleteHandler,
    userAddressDeleteHandler,
    userContactInfoUpdateHandler,
  }
}

export default Contact
